import { useEffect } from "react";
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  Radio
} from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { t } from "i18next";
import TimeValues from "../../../../helpers/constants/TimeValues";
import { validateIP } from "../../../../helpers/functions/functions";
import MDTypography from "../../../../components/MDs/MDTypography";
import InputPlus from "../../../../components/InputPlus";
import SelectAnycast from "./components/selectAnycast";

function AddDomainForm({ formData, setFormData, errors, setErrors, isEdit }) {
  const {
    domain,
    srcip,
    srcport,
    srchttpsport,
    httpport,
    redirecthttp,
    minuses,
    prxvalidnum,
    prxvalidunit,
    ishttps,
    httpsport,
    ishttp2,
    sminuses,
    sprxvalidnum,
    sprxvalidunit,
    certmanaged,
    anycastip
  } = formData;

  const handleChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: null
    });
  };

  const handleCheckChange = (name, event) => {
    const isChecked = event.target.checked ? "1" : "0";
    handleChange(name, isChecked);
  };

  // copy http form into https
  const copyHttp = () => {
    setFormData({
      ...formData,
      sminuses: minuses,
      sprxvalidnum: prxvalidnum,
      sprxvalidunit: prxvalidunit
    });
    setErrors({
      ...errors,
      sminuses: null,
      sprxvalidnum: null,
      sprxvalidunit: null
    });
  }

  // checking ip validation onChange
  useEffect(() => {
    if (!validateIP(srcip) && srcip !== "") {
      setErrors({
        ...errors,
        "srcip": t("message.format", { item: t("domain.source_ip") })
      });
    }
  }, [srcip]);

  useEffect(() => {
    // enable ishttps when redirecthttp is enabled
    if (redirecthttp === "1" && ishttps === "0") {
      handleChange("ishttps", "1");
    }
  }, [redirecthttp]);

  useEffect(() => {
    // disable redirecthttp when ishttps is disabled
    if (ishttps === "0" && redirecthttp === "1") {
      handleChange("redirecthttp", "0");
    }
  }, [ishttps]);

  useEffect(() => {
    // remove cert file if cert-managed is enabled
    if (certmanaged === "1") {
      handleChange("cert", "");
    }
  }, [certmanaged]);

  return (
    <>
      <Grid container rowSpacing={3}>
        <InputPlus
           items={domain}
           error={errors.domain}
           label={t("domain.domain_address")}
           classes={"ltr-textfield"}
           disabled={isEdit}
           handleChange={(e) => handleChange("domain", e)}
        />
      </Grid>

      <Grid container mt={{ md: 3, xs: 3 }} rowSpacing={{ md: 0, xs: 2 }}>
        <Grid item md={4} xs={12} mr={{ md: 1, xs: 0 }}>
          <SelectAnycast
             value={anycastip}
             onChange={(e) => handleChange("anycastip", e.target.value)}
             error={errors.anycastip}
          />
        </Grid>

        <Grid item md={4} xs={12} mr={{ md: 1, xs: 0 }}>
          <TextField id="srcip" label={t("domain.source_ip")} variant="outlined" fullWidth
                     value={srcip} error={errors.srcip} helperText={errors.srcip} className="ltr-textfield"
                     onChange={(e) => handleChange("srcip", e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container mt={{ md: 3, xs: 3 }} rowSpacing={{ md: 0, xs: 2 }}>
        <Grid item md={3} xs={12} mr={{ md: 1, xs: 0 }}>
          <TextField id="srcport" label={t("domain.http_src_port")} variant="outlined" type="number" fullWidth
                     value={srcport} error={errors.srcport} helperText={errors.srcport}
                     onChange={(e) => handleChange("srcport", e.target.value)}
          />
        </Grid>

        <Grid item md={3} xs={12}>
          <TextField id="srchttpsport" label={t("domain.https_src_port")} variant="outlined" type="number" fullWidth
                     value={srchttpsport} error={errors.srchttpsport} helperText={errors.srchttpsport}
                     onChange={(e) => handleChange("srchttpsport", e.target.value)}
          />
        </Grid>
      </Grid>

      <div className="divider-div">
        <Divider />
        <div className="d-flex align-items-center">
          <MDTypography variant="h6" fontWeight="medium">
            HTTP
          </MDTypography>
          <ChevronLeft />
        </div>
      </div>

      <Grid container>
        <Grid item md={4} xs={12} >
          <TextField id="httpport" label={t("domain.port")} variant="outlined" inputProps={{ min: 0 }} fullWidth
                     value={httpport} error={errors.httpport} helperText={errors.httpport} type="number"
                     onChange={(e) => handleChange("httpport", e.target.value)}
          />
        </Grid>

        <Grid item md={6} mt={{ md: 0, xs: 2 }} className="checkbox-container">
          <FormControlLabel
            control={<Checkbox
              checked={redirecthttp === "1"}
              onChange={(e) => handleCheckChange("redirecthttp", e)}
            />}
            label={t("domain.redirect_https")} className="checkbox"
          />
        </Grid>
      </Grid>

      {redirecthttp === "0" &&
        <Grid container mt={3} ml={3}>
          <Grid item md={4} xs={12}>
            <TextField id="minuses" label={t("domain.cache_num_request")} variant="outlined" inputProps={{ min: 0 }} fullWidth
                       value={minuses} error={errors.minuses} helperText={errors.minuses} type="number"
                       onChange={(e) => handleChange("minuses", e.target.value)}
            />

            <FormControl fullWidth>
              <Grid container mt={2}>
                <Grid item md={6} xs={6} pr={1}>
                  <TextField id="prxvalidnum" label={t("domain.valid_time")} variant="outlined" inputProps={{ min: 0 }} fullWidth
                             value={prxvalidnum} error={errors.prxvalidnum} type="number"
                             onChange={(e) => handleChange("prxvalidnum", e.target.value)}
                  />
                </Grid>

                <Grid item md={6} xs={6}>
                  <Select
                    id="prxvalidunit"
                    value={prxvalidunit}
                    onChange={(e) => handleChange("prxvalidunit", e.target.value)}
                    error={errors.prxvalidunit}
                    fullWidth
                  >
                    {TimeValues.map(({ name, value }, index) => {
                      return <MenuItem value={value} key={index}>{name}</MenuItem>
                    })}
                  </Select>
                </Grid>
              </Grid>

              <FormHelperText error={errors.prxvalidunit || errors.prxvalidnum}>
                {errors.prxvalidunit
                  ? errors.prxvalidunit
                  : (errors.prxvalidnum
                      ? errors.prxvalidnum
                      : t("domain.valid_time_helper_text")
                  )
                }
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      }

      <div className="divider-div">
        <Divider />
        <div className="d-flex align-items-center">
          <MDTypography variant="h6" fontWeight="medium">
            HTTPS
          </MDTypography>
          <ChevronLeft />
          <Switch
            checked={ishttps === "1"} color="info"
            onChange={(e) => handleCheckChange("ishttps", e)}
            className="me-1 mb-0"
          />
        </div>
      </div>

      {ishttps === "1" &&
        <Grid container>
          <Grid item md={4}>
            <TextField id="httpsport" label={t("domain.port")} variant="outlined" inputProps={{ min: 0 }} fullWidth
                       value={httpsport} error={errors.httpsport} helperText={errors.httpsport} type="number"
                       onChange={(e) => handleChange("httpsport", e.target.value)}
            />
          </Grid>

          <Grid item md={6} className="checkbox-container">
            <FormControlLabel
              control={<Checkbox
                checked={ishttp2 === "1"}
                onChange={(e) => handleCheckChange("ishttp2", e)}
              />}
              label={t("domain.http2")} className="checkbox"
            />
          </Grid>

          <Grid container mt={3} ml={3} alignItems="center">
            <Grid item md={4} xs={12}>
              <TextField id="sminuses" label={t("domain.cache_num_request")} variant="outlined" inputProps={{ min: 0 }} fullWidth
                         value={sminuses} error={errors.sminuses} helperText={errors.sminuses} type="number"
                         onChange={(e) => handleChange("sminuses", e.target.value)}
              />

              <FormControl fullWidth>
                <Grid container mt={2}>
                  <Grid item md={6} xs={6} pr={1}>
                    <TextField id="sprxvalidnum" label={t("domain.valid_time")} variant="outlined" inputProps={{ min: 0 }} fullWidth
                               value={sprxvalidnum} error={errors.sprxvalidnum} type="number"
                               onChange={(e) => handleChange("sprxvalidnum", e.target.value)}
                    />
                  </Grid>

                  <Grid item md={6} xs={6}>
                    <Select
                      id="sprxvalidunit"
                      value={sprxvalidunit}
                      onChange={(e) => handleChange("sprxvalidunit", e.target.value)}
                      error={errors.sprxvalidunit}
                      fullWidth
                    >
                      {TimeValues.map(({ name, value }, index) => {
                        return <MenuItem value={value} key={index}>{name}</MenuItem>
                      })}
                    </Select>
                  </Grid>
                </Grid>

                <FormHelperText error={errors.sprxvalidnum || errors.sprxvalidunit}>
                  {errors.sprxvalidunit
                    ? errors.sprxvalidunit
                    : (errors.sprxvalidnum
                        ? errors.sprxvalidnum
                        : t("domain.valid_time_helper_text")
                    )
                  }
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item md={4} mt={{ md: 0, xs: 2 }} ml={{ md: 2, xs: 0 }} pb={2}>
              <Button
                variant="outlined" size="small" className="button-fw-normal"
                onClick={copyHttp}
              >
                {t("domain.copy_http")}
              </Button>
            </Grid>
          </Grid>

          <Grid md={4} mt={4}>
            <span className="custom-label ms-2">{t("domain.certificate_management")}:</span>
            <FormControl>
              <RadioGroup
                name="certmanaged"
                value={certmanaged}
                onChange={(e) => handleChange("certmanaged", e.target.value)}
              >
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label={t("domain.certificateـmanaged")}
                  className="custom-radio-label d-flex"
                />
                <FormControlLabel
                  value="0"
                  control={<Radio />}
                  label={t("domain.certificateـmanual")}
                  className="custom-radio-label d-flex"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      }
    </>
  );
}

export default AddDomainForm;
